import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";

import { HoverHintDefinitionGrid } from "@/components/ui/HoverHintDefinitionGrid";
import { apiFetch } from "@/lib/api";

export interface RepAgencyPeekRow {
  id: number;
  name: string;
  fs_account_id: string | null;
  fs_account_name: string | null;
  mv_supplier_name: string | null;
  commission_rate: string | null;
  commission_terminated_at: string | null;
  qbo_vendor_id: string | null;
  integration: {
    has_freshsales_id: boolean;
    has_megaventory_supplier: boolean;
    has_megaventory_customer: boolean;
  };
}

function formatRate(raw: string | null): string {
  if (raw == null || raw.trim() === "") {
    return "—";
  }
  const value = Number(raw);
  if (!Number.isFinite(value)) {
    return raw;
  }
  return `${(value * 100).toFixed(2).replace(/\.00$/, "")}%`;
}

function detailField(value: ReactNode): ReactNode {
  if (value == null) {
    return "—";
  }
  if (typeof value === "string" && value.trim() === "") {
    return "—";
  }
  return value;
}

function RepAgencyPeekBody({ row }: { row: RepAgencyPeekRow }): JSX.Element {
  return (
    <HoverHintDefinitionGrid
      rows={[
        { label: "Rep agency", value: detailField(row.name) },
        { label: "Base rate", value: formatRate(row.commission_rate) },
        { label: "Freshsales", value: detailField(row.fs_account_name) },
        { label: "MV supplier", value: detailField(row.mv_supplier_name) },
        { label: "QBO vendor", value: detailField(row.qbo_vendor_id) },
        {
          label: "Links",
          value: [
            row.integration.has_freshsales_id ? "Freshsales" : null,
            row.integration.has_megaventory_supplier ? "Megaventory supplier" : null,
            row.integration.has_megaventory_customer ? "Megaventory customer" : null,
          ]
            .filter(Boolean)
            .join(" · ") || "—",
        },
        { label: "Terminated", value: detailField(row.commission_terminated_at) },
      ]}
    />
  );
}

export function RepAgencyDetailHoverHint({ repAgencyId }: { repAgencyId: number }): JSX.Element {
  const detailQuery = useQuery({
    queryKey: ["inside-sales", "rep-agency", repAgencyId],
    queryFn: () => apiFetch<{ row: RepAgencyPeekRow }>(`/api/inside-sales/rep-agencies/${repAgencyId}`),
    staleTime: 60_000,
  });

  if (detailQuery.isLoading) {
    return (
      <p className="flex items-center gap-1.5 text-xs text-slate-600">
        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
        Loading rep agency…
      </p>
    );
  }

  if (detailQuery.isError || detailQuery.data?.row == null) {
    return <p className="text-xs text-slate-600">Could not load rep agency preview.</p>;
  }

  return <RepAgencyPeekBody row={detailQuery.data.row} />;
}

export function buildInsideSalesRepAgencyPath(repAgencyId: number): string {
  return `/inside-sales/rep-agencies?agencyId=${String(repAgencyId)}`;
}
